"use client"

import { roomLib, ticketLib } from "@agentcall/core/lib"
import { ticketType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import * as z from "zod"
import { propertyApi } from "@/api/client/property"
import { roomApi } from "@/api/client/room"
import { ticketApi, ticketSchemas } from "@/api/client/ticket"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLabel,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { Textarea } from "@/components/ui/textarea/textarea"

// オーナー向けに表示するカテゴリ（一部は除外）
const OWNER_TICKET_CATEGORIES = ticketType.TICKET_CATEGORIES.filter(
  (cat) =>
    ![
      ticketType.TICKET_CATEGORY_SHINKI_NYUKYOSHA_SHONIN,
      ticketType.TICKET_CATEGORY_KAIYAKU_UKETSUKE,
    ].includes(cat.key as typeof ticketType.TICKET_CATEGORY_SHINKI_NYUKYOSHA_SHONIN)
)

// オーナー向けに表示するステータス
const OWNER_TICKET_STATUSES = ticketType.TICKET_STATUSES.filter(
  (status) => status.key !== ticketType.TICKET_STATUS_COMPLETED
)

const ROOM_NOT_SPECIFIED_VALUE = "__room_not_specified__"

// オーナー向けカテゴリのenum
const ownerCategoryEnum = z.enum([
  ticketType.TICKET_CATEGORY_TOIAWASE,
  ticketType.TICKET_CATEGORY_KAIYAKU_KANREN,
  ticketType.TICKET_CATEGORY_KUREMU_HADO,
  ticketType.TICKET_CATEGORY_KUREMU_SOFUTO,
  ticketType.TICKET_CATEGORY_SONOTA,
  ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE,
])

// フォームスキーマ（サービスについての場合は大カテゴリ、タイトル、お問合せ内容のみ必須）
const addTicketFormSchema = z
  .object({
    category: ownerCategoryEnum,
    subCategory: ticketSchemas.ticketCreateBody.shape.subCategory.optional(),
    title: z.string().trim().min(1, "案件タイトルを入力してください"),
    content: z.string().min(1, "内容を入力してください"),
    propertyId: z.number().nullable().optional(),
    roomId: z.number().nullable().optional(),
    status: ticketSchemas.ticketCreateBody.shape.status,
  })
  .superRefine((data, ctx) => {
    const isServiceCategory = data.category === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE

    // サービスについて以外の場合はpropertyIdとsubCategoryが必須
    if (!isServiceCategory) {
      if (!data.propertyId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "物件を選択してください",
          path: ["propertyId"],
        })
      }
      if (!data.subCategory) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "中カテゴリを選択してください",
          path: ["subCategory"],
        })
      }
    }
  })

type AddTicketFormValues = z.infer<typeof addTicketFormSchema>

export function AddTicketPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isPlanWarningDialogOpen, setIsPlanWarningDialogOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<AddTicketFormValues | null>(null)
  const defaultCategory =
    searchParams.get("category") === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE
      ? ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE
      : undefined

  // 物件一覧取得
  const { data: propertyData } = propertyApi.usePropertyList({ limit: 1000 })
  const properties = propertyData?.properties || []

  // フォーム
  const form = useForm<AddTicketFormValues>({
    resolver: zodResolver(addTicketFormSchema),
    mode: "onChange",
    defaultValues: {
      category: defaultCategory,
      subCategory:
        defaultCategory === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE
          ? ticketType.TICKET_SUBCATEGORY_SABISU_TOIAWASE
          : undefined,
      title: "",
      content: "",
      propertyId: undefined,
      roomId: null,
      status: ticketType.TICKET_STATUS_OC_IN_PROGRESS,
    },
  })

  const watchCategory = form.watch("category")
  const watchPropertyId = form.watch("propertyId")
  const watchRoomId = form.watch("roomId")
  const isServiceCategory = watchCategory === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE

  // 部屋一覧取得（物件が選択されている場合）
  const { data: roomData } = roomApi.useRoomList(
    { propertyId: watchPropertyId ?? 0, withPlans: "true" },
    { query: { enabled: !!watchPropertyId } }
  )
  const rooms = roomData?.rooms || []
  const selectedRoom = rooms.find((room) => room.id === watchRoomId)
  const isSelectedRoomPlanOutside =
    !isServiceCategory && !!watchRoomId && !!selectedRoom && !roomLib.roomActivePlan(selectedRoom)

  // 中カテゴリオプション
  const subCategoryOptions = watchCategory
    ? ticketLib.getSubcategoriesByCategory(watchCategory)
    : []

  // チケット作成
  const ticketCreateMutation = ticketApi.useTicketCreate({
    mutation: {
      onSuccess: () => {
        toast.success("案件を登録しました。")
      },
      onError: (error) => {
        toast.error("案件の登録に失敗しました。", { description: getErrorMessage(error) })
      },
    },
  })

  // カテゴリ変更時のハンドラ
  const handleCategoryChange = (value: string) => {
    form.setValue("category", value as AddTicketFormValues["category"])
    form.setValue("subCategory", undefined as unknown as AddTicketFormValues["subCategory"])

    // サービスについての場合はサブカテゴリを自動設定
    if (value === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE) {
      form.setValue(
        "subCategory",
        ticketType.TICKET_SUBCATEGORY_SABISU_TOIAWASE as AddTicketFormValues["subCategory"]
      )
      form.setValue("status", ticketType.TICKET_STATUS_OC_IN_PROGRESS)
      form.setValue("roomId", null)
    }
  }

  // 物件変更時のハンドラ
  const handlePropertyChange = (value: string) => {
    form.setValue("propertyId", Number(value))
    form.setValue("roomId", null)
  }

  const createTicket = async (values: AddTicketFormValues) => {
    try {
      // サービスについての場合はsubCategoryを自動設定
      const subCategory =
        values.category === ticketType.TICKET_CATEGORY_SABISU_NI_TSUITE
          ? ticketType.TICKET_SUBCATEGORY_SABISU_TOIAWASE
          : (values.subCategory ?? ticketType.TICKET_CATEGORY_SONOTA)

      await ticketCreateMutation.mutateAsync({
        data: {
          category: values.category,
          subCategory,
          title: values.title,
          content: values.content,
          propertyId: values.propertyId ?? null,
          roomId: values.roomId ?? null,
          status: values.status,
        },
      })
      navigate("/tickets", {
        state: { shouldRefetch: true },
      })
    } catch (error) {
      console.error("Error creating ticket:", error)
    }
  }

  const handleSubmit = async (values: AddTicketFormValues) => {
    if (isSelectedRoomPlanOutside) {
      setPendingValues(values)
      setIsPlanWarningDialogOpen(true)
      return
    }

    await createTicket(values)
  }

  const handlePlanWarningDialogChange = (open: boolean) => {
    setIsPlanWarningDialogOpen(open)
    if (!open) {
      setPendingValues(null)
    }
  }

  const handleConfirmCreate = async () => {
    if (!pendingValues) return
    setIsPlanWarningDialogOpen(false)
    await createTicket(pendingValues)
    setPendingValues(null)
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-5">
        <Button variant="outline" size={"sm"} onClick={() => navigate("/tickets")}>
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-semibold">戻る</span>
        </Button>
        <h1 className="text-base font-semibold text-primary">新規案件の作成</h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <Card>
            <CardContent className="pt-5 space-y-4">
              {/* 大カテゴリ */}
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      大カテゴリ
                      <FormRequiredLabel />
                    </FormLabel>
                    <Select onValueChange={handleCategoryChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択してください" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OWNER_TICKET_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.key} value={cat.key}>
                            {cat.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 中カテゴリ（サービスについて以外の場合） */}
              {!isServiceCategory && watchCategory && (
                <FormField
                  control={form.control}
                  name="subCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">
                        中カテゴリ
                        <FormRequiredLabel />
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {subCategoryOptions.map((subCat) => (
                            <SelectItem key={subCat.key} value={subCat.key}>
                              {subCat.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* 案件タイトル */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      案件タイトル
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="エアコンの修理について" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 物件（サービスについて以外の場合） */}
              {!isServiceCategory && (
                <FormField
                  control={form.control}
                  name="propertyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">
                        物件
                        <FormRequiredLabel />
                      </FormLabel>
                      <Select
                        onValueChange={handlePropertyChange}
                        value={field.value?.toString() || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="物件を選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {properties.map((property) => (
                            <SelectItem key={property.id} value={property.id.toString()}>
                              {property.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      {isSelectedRoomPlanOutside && (
                        <p className="text-xs font-medium text-red-500">
                          当該部屋はプラン期間外です
                        </p>
                      )}
                    </FormItem>
                  )}
                />
              )}

              {/* 部屋（サービスについて以外の場合） */}
              {!isServiceCategory && watchPropertyId && (
                <FormField
                  control={form.control}
                  name="roomId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">部屋</FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(value === ROOM_NOT_SPECIFIED_VALUE ? null : Number(value))
                        }
                        value={field.value?.toString() || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="部屋を選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ROOM_NOT_SPECIFIED_VALUE}>部屋を指定しない</SelectItem>
                          {rooms.map((room) => (
                            <SelectItem key={room.id} value={room.id.toString()}>
                              {room.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* 連絡内容/お問合せ内容 */}
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold flex gap-2 items-center">
                      {isServiceCategory ? "お問合せ内容" : "連絡内容"}
                      <FormRequiredLabel />
                    </FormLabel>
                    <FormControl>
                      <Textarea placeholder="" className="min-h-[80px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ステータス */}
              {isServiceCategory ? (
                <FormField
                  control={form.control}
                  name="status"
                  render={() => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">
                        ステータス
                      </FormLabel>
                      <div className="h-10 px-4 py-2 text-sm text-slate-600 bg-white rounded-md">
                        OC対応中
                      </div>
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold flex gap-2 items-center">
                        ステータス
                        <FormRequiredLabel />
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="ステータスを選択してください" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {OWNER_TICKET_STATUSES.map((status) => (
                            <SelectItem key={status.key} value={status.key}>
                              {status.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          {/* 注意書き */}
          <div className="text-sm font-medium text-red-500 leading-5">
            <p>ご登録いただいた案件については順次対応しております。</p>
            <p>急ぎの場合は案件登録後、電話連絡をお願いします。</p>
          </div>

          {/* ボタン */}
          <div className="flex gap-2.5 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate("/tickets")}>
              キャンセル
            </Button>
            <Button
              type="submit"
              disabled={!form.formState.isValid}
              loading={ticketCreateMutation.isPending}
            >
              追加する
            </Button>
          </div>
        </form>
      </Form>

      <Dialog open={isPlanWarningDialogOpen} onOpenChange={handlePlanWarningDialogChange}>
        <DialogContent className="max-w-[420px] rounded-[10px]">
          <DialogHeader>
            <DialogTitle>プラン利用期間外です</DialogTitle>
            <DialogDescription>
              当該物件・部屋は現在プラン利用の期間外です。プラン情報をご確認ください
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handlePlanWarningDialogChange(false)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              onClick={handleConfirmCreate}
              disabled={ticketCreateMutation.isPending}
              loading={ticketCreateMutation.isPending}
            >
              追加する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
